import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Music } from "lucide-react";
import { Link } from "react-router-dom";
import { Track } from "@/types";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useResponsive";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMusic } from "@/contexts/MusicContext";
interface TrackMetadataProps {
  track: Track;
}
export function TrackMetadata({ track }: TrackMetadataProps) {
  const isMobile = useIsMobile();
  const { minimizePlayer } = useMusic();

  // Fetch artist profile to get username for routing
  const { data: artistProfile } = useQuery({
    queryKey: ["artist-profile", track.artistId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_artist_by_id", {
        artist_id: track.artistId,
      });

      if (error) {
        console.error("Error fetching artist profile:", error);
        return null;
      }

      return data?.[0] || null;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Use username for routing if available, otherwise fall back to artistId
  const profileRoute = artistProfile?.username || track.artistId;
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const formatDuration = (duration: number) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  return (
    <div
      className={cn(
        "flex-1 space-y-3",
        isMobile ? "text-left" : "text-center md:text-left",
        isMobile ? "space-y-2" : "space-y-4",
      )}
    >
      <div>
        <h1
          className={cn(
            "font-bold text-white mb-2",
            isMobile ? "text-base" : "text-2xl md:text-3xl",
          )}
        >
          {track.title}
        </h1>
        <p
          className={cn(
            "text-gray-300 mb-3",
            isMobile ? "text-sm" : "text-lg",
            isMobile ? "mb-2" : "mb-4",
          )}
        >
          by{" "}
          <Link
            to={`/profiles/${profileRoute}`}
            className="text-primary-light hover:text-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              minimizePlayer();
            }}
          >
            {track.artist}
          </Link>
          {track.featuredArtists && track.featuredArtists.length > 0 && (
            <span className="text-muted-foreground">
              {" "}· feat. {track.featuredArtists.map((f) => f.name).join(", ")}
            </span>
          )}
        </p>
      </div>

      {/* Track Metadata */}
    </div>
  );
}
